// src/components/Register.js
import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { Eye, EyeOff, Mail, Lock, User, AlertCircle, CheckCircle, Tag } from 'lucide-react';
import {
  trackUserRegistration,
  trackRegistrationAbandonment,
  trackError
} from '../utils/analytics';

// Generate browser fingerprint for registration throttling
function generateFingerprint() {
  const components = [
    navigator.userAgent,
    window.screen.width.toString(),
    window.screen.height.toString(),
    new Date().getTimezoneOffset().toString()
  ];
  return simpleHash(components.join('|'));
}

// Simple hash function to convert fingerprint components into a unique ID
function simpleHash(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return 'fp_' + Math.abs(hash).toString(36);
}

// Check if registration is blocked for this device fingerprint
async function checkRegistrationBlock(fingerprint) {
  try {
    const blockRef = doc(db, 'registrationBlocks', fingerprint);
    const blockDoc = await getDoc(blockRef);

    if (blockDoc.exists()) {
      const blockData = blockDoc.data();
      const blockTime = blockData.timestamp.toMillis();
      const now = Date.now();
      const hoursSinceBlock = (now - blockTime) / (1000 * 60 * 60);

      if (hoursSinceBlock < 24) {
        return { blocked: true, hoursRemaining: Math.ceil(24 - hoursSinceBlock) };
      }
    }
    return { blocked: false };
  } catch (error) {
    console.error('Error checking registration block:', error);
    return { blocked: false }; // Allow registration if check fails
  }
}

// Create registration block to prevent abuse
async function createRegistrationBlock(fingerprint, userId) {
  try {
    const blockRef = doc(db, 'registrationBlocks', fingerprint);
    await setDoc(blockRef, {
      timestamp: serverTimestamp(),
      userId: userId
    });
  } catch (error) {
    console.error('Error creating registration block:', error);
  }
}

export default function Register() {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    promoCode: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [promoStatus, setPromoStatus] = useState({
    isValid: null,
    isValidating: false,
    message: '',
    credits: 0
  });
  
  // Analytics tracking
  const formStartTime = useRef(Date.now());
  const lastInteractedField = useRef(null);
  
  const { signup, signInWithGoogle, getUserCredits } = useAuth();
  const navigate = useNavigate();
  
  // Auto-fill promo code from URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const promoFromUrl = urlParams.get('promo') || urlParams.get('ref');
    if (promoFromUrl) {
      setFormData(prev => ({ ...prev, promoCode: promoFromUrl.toUpperCase() }));
      validatePromoCode(promoFromUrl.toUpperCase());
    }
  }, []);

  // Track form abandonment on unmount
  useEffect(() => {
    return () => {
      if (!success && lastInteractedField.current) {
        const timeSpent = Math.floor((Date.now() - formStartTime.current) / 1000);
        trackRegistrationAbandonment(lastInteractedField.current, timeSpent);
      }
    };
  }, [success]);

  function handleChange(e) {
    lastInteractedField.current = e.target.name;
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    
    // Validate promo code on change
    if (e.target.name === 'promoCode') {
      validatePromoCode(e.target.value);
    }
  }

  // Validate promo code function with LAUNCH format detection
  async function validatePromoCode(code) {
    if (!code || code.length < 3) {
      setPromoStatus({
        isValid: null,
        isValidating: false,
        message: '',
        credits: 0
      });
      return;
    }

    // Client-side format validation for better UX
    const upperCode = code.toUpperCase();
    const isLaunchFormat = /^LAUNCH-[A-Z0-9]{5,6}$/.test(upperCode);
    
    if (code.length >= 6 && !isLaunchFormat) {
      setPromoStatus({
        isValid: false,
        isValidating: false,
        message: 'Promo code format should be: LAUNCH-XXXXX',
        credits: 0
      });
      return;
    }

    setPromoStatus(prev => ({ ...prev, isValidating: true }));

    try {
      const functions = getFunctions();
      const previewPromoCodeFunction = httpsCallable(functions, 'previewPromoCode');
      const result = await previewPromoCodeFunction({ code: upperCode });
      
      setPromoStatus({
        isValid: true,
        isValidating: false,
        message: `${result.data.creditsAdded} bonus credits added!`,
        credits: result.data.creditsAdded
      });
    } catch (error) {
      let errorMessage = 'Invalid promo code';
      
      // Enhanced error messaging based on specific error types
      if (error.message.includes('format')) {
        errorMessage = 'Promo code format should be: LAUNCH-XXXXX';
      } else if (error.message.includes('already used this promo code')) {
        errorMessage = 'You have already used this promo code';
      } else if (error.message.includes('already used a promo code')) {
        errorMessage = 'You can only use one promo code per account';
      } else if (error.message.includes('expired')) {
        errorMessage = 'This promo code has expired';
      } else if (error.message.includes('no longer active')) {
        errorMessage = 'This promo code is no longer active';
      } else if (error.message.includes('usage limit')) {
        errorMessage = 'This promo code has been fully redeemed';
      }
      
      setPromoStatus({
        isValid: false,
        isValidating: false,
        message: errorMessage,
        credits: 0
      });
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    
    if (!formData.email || !formData.password || !formData.confirmPassword) {
      return setError('Please fill in all fields');
    }

    if (formData.password !== formData.confirmPassword) {
      return setError('Passwords do not match');
    }

    if (formData.password.length < 6) {
      return setError('Password must be at least 6 characters');
    }

    // Check registration throttling
    const fingerprint = generateFingerprint();
    const blockCheck = await checkRegistrationBlock(fingerprint);

    if (blockCheck.blocked) {
      return setError(`A registration from this device was recently completed. Please try again in ${blockCheck.hoursRemaining} hours.`);
    }

    try {
      setError('');
      setLoading(true);
      const userCredential = await signup(formData.email, formData.password);

      // Apply promo code if valid
      if (formData.promoCode && promoStatus.isValid) {
        try {
          const functions = getFunctions();
          const validatePromoCodeFunction = httpsCallable(functions, 'validatePromoCode');
          await validatePromoCodeFunction({
            code: formData.promoCode.toUpperCase(),
            isSignup: true // Flag to track this was used during registration
          });
          console.log('✅ Promo code applied successfully during signup');

          // Refresh credits to show updated balance immediately
          await getUserCredits(userCredential.user.uid);
          console.log('✅ Credits refreshed in AuthContext');
        } catch (promoError) {
          console.error('❌ Failed to apply promo code:', promoError);
          // Don't fail registration if promo code fails to apply
        }
      }

      // Create registration block to prevent abuse
      await createRegistrationBlock(fingerprint, userCredential.user.uid);

      setSuccess(true);

      // Track successful registration
      trackUserRegistration('email', userCredential.user.uid, {
        email_domain: formData.email.split('@')[1],
        registration_source: 'direct_signup',
        promo_code_used: formData.promoCode || null,
        promo_credits_received: promoStatus.credits || 0
      });
      
      // Show success message for 2 seconds, then redirect
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
    } catch (error) {
      setError('Failed to create account. Email may already be in use.');
      console.error('Registration error:', error);
      
      // Track registration error
      trackError({
        errorType: 'registration_error',
        errorMessage: error.message || 'Failed to create account',
        component: 'Register',
        additionalContext: {
          email_domain: formData.email ? formData.email.split('@')[1] : 'unknown',
          registration_method: 'email'
        }
      });
    }
    
    setLoading(false);
  }

  async function handleGoogleSignUp() {
    // Check registration throttling
    const fingerprint = generateFingerprint();
    const blockCheck = await checkRegistrationBlock(fingerprint);

    if (blockCheck.blocked) {
      return setError(`A registration from this device was recently completed. Please try again in ${blockCheck.hoursRemaining} hours.`);
    }

    try {
      setError('');
      setLoading(true);
      const userCredential = await signInWithGoogle();

      // Apply promo code if valid
      if (formData.promoCode && promoStatus.isValid) {
        try {
          const functions = getFunctions();
          const validatePromoCodeFunction = httpsCallable(functions, 'validatePromoCode');
          await validatePromoCodeFunction({
            code: formData.promoCode.toUpperCase(),
            isSignup: true // Flag to track this was used during registration
          });
          console.log('✅ Promo code applied successfully during signup');

          // Refresh credits to show updated balance immediately
          await getUserCredits(userCredential.user.uid);
          console.log('✅ Credits refreshed in AuthContext');
        } catch (promoError) {
          console.error('❌ Failed to apply promo code:', promoError);
          // Don't fail registration if promo code fails to apply
        }
      }

      // Create registration block to prevent abuse
      await createRegistrationBlock(fingerprint, userCredential.user.uid);

      // Track Google registration
      trackUserRegistration('google', userCredential.user.uid, {
        email_domain: userCredential.user.email.split('@')[1],
        registration_source: 'google_oauth',
        promo_code_used: formData.promoCode || null,
        promo_credits_received: promoStatus.credits || 0
      });
      
      navigate('/dashboard');
    } catch (error) {
      setError('Failed to sign up with Google');
      console.error('Google sign-up error:', error);
      
      // Track Google registration error
      trackError({
        errorType: 'google_registration_error',
        errorMessage: error.message || 'Failed to sign up with Google',
        component: 'Register',
        additionalContext: {
          registration_method: 'google'
        }
      });
    }
    
    setLoading(false);
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8 text-center">
          <div className="mx-auto h-16 w-16 flex items-center justify-center rounded-full bg-green-100">
            <CheckCircle className="h-10 w-10 text-green-600" />
          </div>
          <div>
            <h2 className="text-3xl font-extrabold text-gray-900">Account Created!</h2>
            <p className="mt-4 text-gray-600">
              Please check your email to verify your account.
              <br />
              You've received {5 + promoStatus.credits} free credits to get started!
              {promoStatus.credits > 0 && (
                <span className="block mt-2 text-green-600 font-medium">
                  ✓ Promo code applied: +{promoStatus.credits} bonus credits!
                </span>
              )}
            </p>
            <p className="mt-4 text-sm text-gray-500">
              Redirecting to dashboard...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div>
          <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-green-100">
            <User className="h-8 w-8 text-green-600" />
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Create your account
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Already have an account?{' '}
            <Link
              to="/login"
              className="font-medium text-green-600 hover:text-green-500"
            >
              Sign in here
            </Link>
          </p>
        </div>

        {/* Form */}
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <div className="flex">
                <AlertCircle className="h-5 w-5 text-red-400" />
                <div className="ml-3">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-4">
            {/* Email Field */}
            <div>
              <label htmlFor="email-register" className="block text-sm font-medium text-gray-700">
                Email address
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="email-register"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className="pl-10 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-green-500 focus:border-green-500"
                  placeholder="Enter your email"
                  value={formData.email}
                  onChange={handleChange}
                />
              </div>
            </div>

            {/* Password Field */}
            <div>
              <label htmlFor="password-register" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="password-register"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  required
                  className="pl-10 pr-10 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-green-500 focus:border-green-500"
                  placeholder="Create a password (min 6 characters)"
                  value={formData.password}
                  onChange={handleChange}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
            </div>

            {/* Confirm Password Field */}
            <div>
              <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700">
                Confirm Password
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="confirm-password"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  required
                  className="pl-10 pr-10 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-green-500 focus:border-green-500"
                  placeholder="Confirm your password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
            </div>

            {/* Promo Code Field */}
            <div>
              <label htmlFor="promo-code" className="block text-sm font-medium text-gray-700">
                Promo Code (Optional)
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Tag className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="promo-code"
                  name="promoCode"
                  type="text"
                  autoComplete="off"
                  className={`pl-10 block w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-green-500 focus:border-green-500 ${
                    promoStatus.isValid === true ? 'border-green-300 bg-green-50' :
                    promoStatus.isValid === false ? 'border-red-300 bg-red-50' :
                    'border-gray-300'
                  }`}
                  placeholder="e.g., LAUNCH-ABC12"
                  value={formData.promoCode}
                  onChange={handleChange}
                />
                {promoStatus.isValidating && (
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-500"></div>
                  </div>
                )}
              </div>
              
              {/* Promo Code Status Messages */}
              {promoStatus.message && (
                <div className={`mt-2 text-sm flex items-center ${
                  promoStatus.isValid ? 'text-green-600' : 'text-red-600'
                }`}>
                  {promoStatus.isValid ? (
                    <CheckCircle className="h-4 w-4 mr-1" />
                  ) : (
                    <AlertCircle className="h-4 w-4 mr-1" />
                  )}
                  {promoStatus.message}
                </div>
              )}
              
              {/* Format Helper Text */}
              {!promoStatus.message && !formData.promoCode && (
                <div className="mt-2 text-xs text-gray-500">
                  Launch campaign codes follow format: LAUNCH-XXXXX
                </div>
              )}
            </div>
          </div>

          {/* Benefits Notice */}
          <div className="bg-green-50 border border-green-200 rounded-md p-4">
            <div className="flex">
              <CheckCircle className="h-5 w-5 text-green-400" />
              <div className="ml-3">
                <p className="text-sm text-green-800">
                  <strong>Welcome bonus:</strong> Get {5 + promoStatus.credits} free credits when you sign up!
                  {promoStatus.credits > 0 && (
                    <span className="block mt-1">
                      ✓ Includes {promoStatus.credits} bonus credits from your promo code!
                    </span>
                  )}
                </p>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Creating account...' : 'Create Account'}
            </button>
          </div>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-gray-50 text-gray-500">Or sign up with</span>
            </div>
          </div>

          {/* Google Sign-up Button */}
          <div>
            <button
              type="button"
              onClick={handleGoogleSignUp}
              disabled={loading}
              className="w-full inline-flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path
                  fill="currentColor"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="currentColor"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="currentColor"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="currentColor"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              Sign up with Google
            </button>
          </div>

          {/* Terms Notice */}
          <p className="text-xs text-gray-500 text-center">
            By creating an account, you agree to our{' '}
            <a href="#" className="text-green-600 hover:text-green-500">Terms of Service</a>
            {' '}and{' '}
            <a href="#" className="text-green-600 hover:text-green-500">Privacy Policy</a>
          </p>
        </form>
      </div>
    </div>
  );
}